import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { map, Observable } from 'rxjs';

import {
  RefundResponse,
  RefundTransactionRequest,
  SignatureCaptureRequest,
  SignatureCaptureResult,
  TransactionFilter,
  TransactionList
} from 'src/app/models';
import { BaseService } from '../base.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { ApiType } from 'src/app/pos.config';

@Injectable({
  providedIn: 'root'
})
export class TransactionLookupService extends BaseService {
  constructor(
    store: Store,
    private http: HttpClient,
    private readonly errorService: ErrorService
  ) {
    super(store);
  }

  getTransactionUrl(id?) {
    const url = 'transactions';
    if (id) {
      return `${url}/${id}`;
    }
    return url;
  }

  getTransactions(
    params: Partial<TransactionFilter>
  ): Observable<TransactionList[]> {
    return this.http.get<TransactionList[]>(this.getTransactionUrl(), {
      params
    });
  }

  getMealPlanTransactions(
    params: Partial<TransactionFilter>
  ): Observable<TransactionList[]> {
    return this.http
      .get<{
        success: boolean;
        transactions: TransactionList[];
      }>('mealplan_transactions', { params })
      .pipe(
        map((response) => (response?.success ? response.transactions : []))
      );
  }

  refund(
    ticket_uuid: string,
    refund_operator_id: number,
    approving_refund_operator_id: number | null | undefined,
    ticket_items_uuid: string[],
    refund_reason_id?: number,
    refund_reason?: string
  ) {
    let requestBody: any = { pos_operator_id: refund_operator_id };
    if (approving_refund_operator_id != null) {
      requestBody.approving_refund_operator_id = approving_refund_operator_id;
    }
    if (ticket_items_uuid?.length) {
      requestBody = {
        ...requestBody,
        individual_ticket_item_ids: ticket_items_uuid.join(',')
      };
    }
    if (refund_reason_id != null) {
      requestBody.refund_reason_id = refund_reason_id;
    }
    if (refund_reason) {
      requestBody.refund_reason = refund_reason;
    }

    return this.http
      .post<RefundResponse>(
        this.getLocationUrl(`tickets/${ticket_uuid}/refund`),
        null,
        { params: requestBody }
      )
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  /**
   * POS-972 On-Screen Signature — Bill to Room / Charge Account.
   * `PATCH api/pos/v3/locations/:location_id/transactions/:id/signature` with either
   * `{ signature_data }` or `{ skip: true }` — this route is nested under `:location_id`
   * (see touch-backend's `routes.rb`), unlike the plain `transactions/:id` index/show routes,
   * so it must go through `getLocationUrl` rather than `getTransactionUrl`.
   */
  patchTransactionSignature(
    transactionId: number,
    payload: SignatureCaptureRequest
  ): Observable<SignatureCaptureResult> {
    return this.http
      .patch<{ object: SignatureCaptureResult }>(
        this.getLocationUrl(`transactions/${transactionId}/signature`),
        payload
      )
      .pipe(map((response) => response.object));
  }

  addRefundTransaction(
    ticket_uuid: string,
    refund_request: RefundTransactionRequest,
    pos_operator_id: number
  ) {
    return this.http
      .post<RefundResponse>(
        this.getLocationUrl(`tickets/${ticket_uuid}/add_refund_transaction`),
        null,
        { params: { ...refund_request, pos_operator_id } }
      )
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }
}
